import React from 'react';
import styled from 'styled-components';
import colorTokens from '^/theme/colors/color-tokens';
import theme from '^/theme';
import { HorizontalDivider } from '^/components/atoms/ContentsListItem';
import Button from '^/components/atoms/Button';
import { L10nDictionary } from '^/utilities/l10n';
import { useL10n } from '^/hooks';
import Text from './text';
import { usePopupStore } from '^/store/popup';
import * as T from '^/types';
interface Permission {
  name: L10nDictionary;
  isEnabled: boolean;
}

interface Role {
  name: L10nDictionary;
  permissions: Permission[];
}

interface Props {
  roles: Role[];
}

const Container = styled.div`
  width: 100%;
  padding-top: 20px;
`;

const Title = styled.p`
  color: ${colorTokens.textCoolLight};
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 24px;
`;

const RoleContainer = styled.div`
  display: flex;
  gap: 16px;
  padding-bottom: 20px;
`;

const RoleCard = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  transition: all 0.2s ease;
`;

const RoleName = styled.span`
  color: ${colorTokens.textCoolDark};
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  width: 100px;
`;

const PermissionList = styled.div`
  display: flex;
  padding: 4px;
  flex-wrap: wrap;
  font-size: 12px;
  border-radius: 5px;
  color: ${theme.colors.coolGray[600]};
  background-color: ${colorTokens.bgLightGray};
`;
const VerticalDivider = styled.div({
  width: '2px',
  backgroundColor: theme.colorTokens.lineCool,
});

const RoleContainerWrapper = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 20px;
`;

interface RoleInformationProps extends Props {
  isEditProject?: boolean;
  showConfirmButton?: boolean;
}

const RoleInformation: React.FC<RoleInformationProps> = ({
  roles,
  isEditProject,
  showConfirmButton,
}) => {
  const [l10n] = useL10n();
  const setPopup = usePopupStore(s => s.setMinorPopup);
  const setPopupOptions = usePopupStore(s => s.setPopupOptions);
  const onConfirmClickHandler = () => {
    if (isEditProject) {
      setPopup(T.ContentPageMinorPopupType.MANAGE_PROJECT_POPUP);
      setPopupOptions({ manageProjectPopupType: T.ManageProjectPopupType.ADD_COLLABORATOR });
    } else {
      setPopup();
      setPopupOptions({ projectPagePopupType: undefined });
    }
  };
  return (
    <Container>
      <Title>{l10n(Text.userRolesAndPermissions)}</Title>
      <RoleContainer>
        <VerticalDivider />
        <RoleContainerWrapper>
          {roles.map(role => (
            <RoleCard key={l10n(role.name)}>
              <RoleName>{l10n(role.name)}</RoleName>
              <PermissionList>
                {role.permissions.map((permission, index) =>
                  index === role.permissions.length - 1
                    ? `${l10n(permission.name)}`
                    : `${l10n(permission.name)}, `
                )}
              </PermissionList>
            </RoleCard>
          ))}
        </RoleContainerWrapper>
      </RoleContainer>
      {showConfirmButton && (
        <>
          <HorizontalDivider customStyles={{ marginTop: '0', marginBottom: '0' }} />
          <ButtonWrapper>
            <Button variant="secondary" size="large" onClick={onConfirmClickHandler}>
              {l10n(Text.confirm)}
            </Button>
          </ButtonWrapper>
        </>
      )}
    </Container>
  );
};

export default RoleInformation;
